import { parseEntityRef } from '@backstage/catalog-model';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import useSWR from 'swr';
import { FeatureFlag } from '../featureFlags';
import { useOptionalLDClient } from './useOptionalLDClient';

export type AsyncFlagResult = {
  loading: boolean;
  enabled: boolean;
  error?: Error;
};

export type AsyncFlagValue = {
  loading: boolean;
  value: unknown;
  error?: Error;
};

// SWR, matching the other flag hooks: the sidebar reads flags outside plugin QueryClientProviders.
export function useFeatureFlagUserValue(flag: FeatureFlag): AsyncFlagValue {
  const identityApi = useApi(identityApiRef);
  const ldClient = useOptionalLDClient();

  const { data, error } = useSWR(flag, async () => {
    if (!ldClient) {
      return false;
    }

    const [profile, backstageIdentity] = await Promise.all([
      identityApi.getProfileInfo(),
      identityApi.getBackstageIdentity(),
    ]);

    const launchDarklyContext = {
      kind: 'aura-portal-user',
      key: parseEntityRef(backstageIdentity.userEntityRef).name,
      name: profile.displayName,
      email: profile.email,
      groups: backstageIdentity.ownershipEntityRefs.map(
        ref => parseEntityRef(ref).name,
      ),
    };

    const current = ldClient.getContext?.();
    const shouldSwitch =
      !current ||
      (current as any).key !== launchDarklyContext.key ||
      (current as any).kind !== launchDarklyContext.kind;

    // only re-identify if different - to make mocking flags during tests possible
    if (shouldSwitch) {
      await ldClient.identify(launchDarklyContext);
    }

    return ldClient.variation(flag, false);
  });

  if (error) {
    return { error, loading: false, value: undefined };
  }
  if (data === undefined) {
    return { loading: true, value: undefined };
  }
  return { loading: false, value: data };
}

export function useFeatureFlagUserVariation(
  flag: FeatureFlag,
): AsyncFlagResult {
  const { value, loading, error } = useFeatureFlagUserValue(flag);

  if (error) {
    return { error, loading: false, enabled: false };
  }
  if (loading) {
    return { loading: true, enabled: false };
  }
  return { loading: false, enabled: Boolean(value) };
}
